class FastUTVVehiclePlatform extends HTMLElement {
  #abort;
  #tabs = [];
  #panels = [];
  #trigger;
  #card;
  #pinned = false;
  #closeTimer;
  #suppressFocus = false;
  #audio;
  #soundEnabled = false;
  #nodes = new Set();
  #triggers = [];
  #resize;
  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#triggers = [...this.querySelectorAll('[data-hotspot-trigger]')];
    this.#tabs = [...this.querySelectorAll('[data-category-tab]')];
    this.#panels = [...this.querySelectorAll('[data-category-panel]')];
    if (!this.#tabs.length || this.#tabs.length !== this.#panels.length) return;
    const ids = this.#panels.map(panel => panel.id);
    if (new Set(ids).size !== ids.length || this.#tabs.some(tab => !ids.includes(tab.hash.slice(1)))) return;
    this.querySelector('[data-category-tabs]')?.setAttribute('role', 'tablist');
    for (const tab of this.#tabs) {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', tab.hash.slice(1));
    }
    for (const panel of this.#panels) {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', `${panel.id}-tab`);
      panel.tabIndex = 0;
    }
    this.addEventListener('click', event => this.#click(event), { signal });
    this.addEventListener('keydown', event => this.#key(event), { signal });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.#trigger) this.#key(event);
    }, { signal });
    this.addEventListener('pointerover', event => {
      if (event.pointerType !== 'mouse') return;
      const trigger = this.#triggerFor(event.target);
      if (trigger && (!this.#pinned || trigger === this.#trigger)) this.#open(trigger, false);
      if (event.target.closest('.ftv-platform__explorer')) clearTimeout(this.#closeTimer);
    }, { signal });
    this.addEventListener('pointerout', event => {
      if (event.pointerType !== 'mouse' || this.#pinned || !this.#trigger) return;
      const explorer = this.querySelector('.ftv-platform__explorer');
      if (explorer?.contains(event.relatedTarget)) return;
      if (explorer?.contains(event.target)) this.#closeTimer = setTimeout(() => {
        if (!this.#card?.contains(document.activeElement) && document.activeElement !== this.#trigger) this.#close();
      }, 450);
    }, { signal });
    this.addEventListener('focusin', event => {
      if (this.#suppressFocus) return;
      const trigger = this.#triggerFor(event.target);
      if (trigger) this.#open(trigger, false);
      if (this.#card?.contains(event.target)) clearTimeout(this.#closeTimer);
    }, { signal });
    this.addEventListener('focusout', event => {
      if (this.#pinned || !this.#trigger) return;
      if (!this.querySelector('[data-explorer-rail]')?.contains(event.relatedTarget) && event.relatedTarget !== this.#trigger) this.#close();
    }, { signal });
    document.addEventListener('pointerdown', event => {
      if (this.#trigger && this.#triggerFor(event.target) !== this.#trigger && !this.querySelector('[data-explorer-rail]')?.contains(event.target)) this.#close();
    }, { signal });
    window.addEventListener('pagehide', () => this.#disableSound(), { signal });
    window.addEventListener('hashchange', () => this.#selectFromHash(), { signal });
    this.addEventListener('shopify:block:select', event => {
      const panel = event.target.closest('[data-category-panel]');
      if (panel) this.#select(panel.id, false);
    }, { signal });
    const plane = this.querySelector('[data-vehicle-plane]');
    if (plane) {
      const setDock = () => { this.dataset.explorerDocked = String(plane.getBoundingClientRect().width < 600); };
      setDock();
      this.#resize?.disconnect();
      this.#resize = new ResizeObserver(setDock);
      this.#resize.observe(plane);
    }
    this.#selectFromHash();
    for (const trigger of this.querySelectorAll('[data-hotspot-trigger]')) trigger.hidden = false;
    this.dataset.platformReady = 'true';
    if (this.#triggers.length) {
      this.dataset.explorerHasZones = 'true';
      this.querySelector('[data-explorer-rail]').hidden = false;
      if (window.AudioContext || window.webkitAudioContext) this.querySelector('[data-explorer-sound]').hidden = false;
    }
  }
  disconnectedCallback() {
    this.#abort?.abort();
    this.#close();
    this.#disableSound();
    this.#resize?.disconnect();
    this.querySelector('[data-explorer-rail]')?.setAttribute('hidden', '');
    this.querySelector('[data-explorer-sound]')?.setAttribute('hidden', '');
    for (const panel of this.#panels) panel.hidden = false;
    for (const trigger of this.querySelectorAll('[data-hotspot-trigger]')) trigger.hidden = true;
    delete this.dataset.platformReady;
  }
  #selectFromHash() {
    let id;
    try { id = decodeURIComponent(location.hash.slice(1)); } catch { id = ''; }
    this.#select(this.#panels.some(panel => panel.id === id) ? id : this.#panels[0].id, false);
  }
  #select(id, persist = true) {
    if (!this.#panels.some(panel => panel.id === id)) return;
    for (const panel of this.#panels) panel.hidden = panel.id !== id;
    for (const tab of this.#tabs) {
      const selected = tab.hash.slice(1) === id;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }
    for (const trigger of this.querySelectorAll('[data-hotspot-trigger]')) trigger.dataset.categoryActive = String(trigger.dataset.categoryTarget === id);
    if (persist) history.replaceState(history.state, '', `${location.pathname}${location.search}#${encodeURIComponent(id)}`);
  }
  #click(event) {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-explorer-sound]')) { this.#toggleSound(event); return; }
    if (event.target.closest('[data-explorer-next], [data-explorer-previous]')) {
      const direction = event.target.closest('[data-explorer-next]') ? 1 : -1;
      const current = this.#triggers.indexOf(this.#trigger);
      const index = current < 0 ? (direction > 0 ? 0 : this.#triggers.length - 1) : (current + direction + this.#triggers.length) % this.#triggers.length;
      if (this.#triggers[index]) this.#open(this.#triggers[index], true);
      this.#tone(event);
      return;
    }
    const trigger = this.#triggerFor(event.target);
    if (trigger) {
      this.#tone(event);
      const wasPinned = this.#trigger === trigger && this.#pinned;
      if (wasPinned) this.#close();
      else this.#open(trigger, true);
      return;
    }
    if (event.target.closest('[data-hotspot-close]')) { const opener = this.#trigger; this.#close(); this.#restoreFocus(opener); return; }
    const link = event.target.closest('[data-category-tab], [data-category-link]');
    if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const id = link.hash.slice(1);
    if (!this.#panels.some(panel => panel.id === id)) return;
    event.preventDefault();
    this.#select(id);
    this.#tone(event);
    if (link.hasAttribute('data-category-link')) {
      this.#close();
      const tab = this.#tabs.find(item => item.hash.slice(1) === id);
      tab?.focus({ preventScroll: true });
      tab?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }
  #key(event) {
    if (event.key === 'Escape' && this.#trigger) {
      event.preventDefault();
      const trigger = this.#trigger;
      this.#close();
      this.#restoreFocus(trigger);
      return;
    }
    const hotspot = event.target.closest('[data-hotspot-trigger]');
    if (hotspot && event.key === 'ArrowDown') {
      event.preventDefault();
      this.#open(hotspot, true);
      this.#card?.querySelector('a, button')?.focus();
      return;
    }
    const tab = event.target.closest('[data-category-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let index = this.#tabs.indexOf(tab);
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = this.#tabs.length - 1;
    else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + this.#tabs.length) % this.#tabs.length;
    const next = this.#tabs[index];
    this.#select(next.hash.slice(1));
    this.#tone(event);
    next.focus({ preventScroll: true });
    next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
  #triggerFor(target) {
    if (!(target instanceof Element)) return null;
    const control = target.closest('[data-hotspot-trigger]');
    if (control) return control;
    const anchor = target.closest('[data-explorer-anchor]');
    return anchor ? this.querySelector(`#${CSS.escape(anchor.dataset.explorerAnchor)}`) : null;
  }
  #restoreFocus(trigger) {
    this.#suppressFocus = true;
    trigger?.focus({ preventScroll: true });
    this.#suppressFocus = false;
  }
  #open(trigger, pinned) {
    clearTimeout(this.#closeTimer);
    if (this.#trigger !== trigger) this.#close();
    const card = this.querySelector(`#${CSS.escape(trigger.getAttribute('aria-controls'))}`);
    if (!card) return;
    this.#trigger = trigger;
    this.#card = card;
    this.#pinned = pinned || this.#pinned;
    card.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this.#select(trigger.dataset.categoryTarget, pinned);
    this.querySelector('[data-explorer-idle]').hidden = true;
    for (const anchor of this.querySelectorAll('[data-explorer-anchor], [data-explorer-leader]')) anchor.dataset.active = String((anchor.dataset.explorerAnchor || anchor.dataset.explorerLeader) === trigger.id);
    const counter = this.querySelector('[data-explorer-counter]');
    counter.textContent = `${String(this.#triggers.indexOf(trigger) + 1).padStart(2, '0')} / ${String(this.#triggers.length).padStart(2, '0')}`;
    this.dataset.explorerOpen = this.#pinned ? 'pinned' : 'preview';
  }
  async #toggleSound(event) {
    if (!event.isTrusted) return;
    if (this.#soundEnabled) { this.#disableSound(); return; }
    const Audio = window.AudioContext || window.webkitAudioContext;
    if (!Audio) return;
    try {
      this.#audio = new Audio();
      this.#soundEnabled = true;
      this.#updateSoundControl();
      await this.#audio.resume();
      this.#tone(event);
    } catch { this.#disableSound(); }
  }
  #updateSoundControl() {
    const button = this.querySelector('[data-explorer-sound]');
    const label = button?.querySelector('[data-sound-label]');
    button?.setAttribute('aria-pressed', String(this.#soundEnabled));
    if (label) label.textContent = this.#soundEnabled ? label.dataset.on : label.dataset.off;
  }
  #tone(event) {
    if (!event?.isTrusted || !this.#soundEnabled || this.#audio?.state !== 'running') return;
    try {
      const oscillator = this.#audio.createOscillator();
      const gain = this.#audio.createGain();
      const now = this.#audio.currentTime;
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(760, now);
      oscillator.frequency.exponentialRampToValueAtTime(430, now + 0.04);
      gain.gain.setValueAtTime(0.025, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
      oscillator.connect(gain);
      gain.connect(this.#audio.destination);
      this.#nodes.add(oscillator);
      this.#nodes.add(gain);
      oscillator.onended = () => {
        oscillator.disconnect(); gain.disconnect();
        this.#nodes.delete(oscillator); this.#nodes.delete(gain);
      };
      oscillator.start(now); oscillator.stop(now + 0.055);
    } catch { this.#disableSound(); }
  }
  #disableSound() {
    this.#soundEnabled = false;
    this.#updateSoundControl();
    for (const node of this.#nodes) { try { node.disconnect(); } catch {} }
    this.#nodes.clear();
    const audio = this.#audio;
    this.#audio = undefined;
    if (audio && audio.state !== 'closed') audio.close().catch(() => {});
  }
  #close() {
    clearTimeout(this.#closeTimer);
    if (this.#card) this.#card.hidden = true;
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.#card = undefined;
    this.#trigger = undefined;
    this.#pinned = false;
    this.querySelector('[data-explorer-idle]')?.removeAttribute('hidden');
    for (const anchor of this.querySelectorAll('[data-explorer-anchor], [data-explorer-leader]')) anchor.dataset.active = 'false';
    delete this.dataset.explorerOpen;
  }
}
if (!customElements.get('fastutv-vehicle-platform')) customElements.define('fastutv-vehicle-platform', FastUTVVehiclePlatform);
